import type { SearchFilterJoin, SearchFilterOperator } from '@/shared/components/filters/searchFilters';
import { COMPANY_ASSET_TYPE_VALUES, LEGACY_COMPANY_ASSET_TYPE_ALIASES } from '@/shared/constants/companyAssetTypes';

import type { UnifiedSearchResult } from './useAllSearch';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const recordValue = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});
const arrayValue = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const selectedStrings = (record: Record<string, unknown>, key: string) =>
    arrayValue(record[key]).filter((value): value is string => typeof value === 'string');
const stringValue = (...values: unknown[]): string | undefined =>
    values.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim();
const numberValue = (...values: unknown[]): number | undefined =>
    values.find((value): value is number => typeof value === 'number' && Number.isFinite(value));
const formatSource = (source: string) =>
    source
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());

const matchesActivePredicates = (predicates: boolean[], filterJoin: SearchFilterJoin) =>
    predicates.length === 0 || (filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean));

export const collectEventRecords = (value: unknown, limit = 60): Record<string, unknown>[] => {
    const collected: Record<string, unknown>[] = [];
    const visit = (candidate: unknown) => {
        if (collected.length >= limit) {
            return;
        }
        if (Array.isArray(candidate)) {
            candidate.forEach(visit);
            return;
        }
        if (!isRecord(candidate)) {
            return;
        }
        const title = stringValue(
            candidate.title,
            candidate.name,
            candidate.companyName,
            candidate.event,
            candidate.symbol,
        );
        const hasEventContext = Boolean(
            stringValue(candidate.date, candidate.publishedDate, candidate.url, candidate.link, candidate.event),
        );
        if (title && hasEventContext) {
            collected.push(candidate);
            return;
        }
        Object.values(candidate).forEach(visit);
    };
    visit(value);

    return collected;
};

export const normalizeEvent = (
    value: unknown,
    source: string,
    index: number,
    domain: 'events' | 'news' = 'news',
): UnifiedSearchResult | undefined => {
    const event = recordValue(value);
    const title = stringValue(event.title, event.name, event.companyName, event.event, event.symbol);
    if (!title) {
        return undefined;
    }
    const url = stringValue(event.url, event.link);
    const publisher = stringValue(event.publisher, event.site, event.source)?.replace(
        /Financial Modeling Prep|\bFMP\b/gi,
        'Platform Financials',
    );

    return {
        description: stringValue(event.text, event.description, event.transactionType),
        domain,
        document:
            domain === 'news'
                ? {
                      source: {
                          canonicalUrl: url,
                          kind: 'news',
                          label: 'News Article',
                          publishedAt: stringValue(event.publishedDate, event.date),
                          publisher,
                          summary: stringValue(event.text, event.description),
                          title,
                      },
                  }
                : undefined,
        external: Boolean(url),
        id: `${domain}:${source}:${stringValue(event.id, event.url, event.link) ?? `${title}:${stringValue(event.date) ?? index}`}`,
        kind: formatSource(source),
        logoUrl: stringValue(event.image, event.imageUrl),
        metadata: [],
        source: [...new Set([publisher, 'Platform Financials'].filter(Boolean))].join(' / '),
        title,
        updatedAt: stringValue(event.publishedDate, event.date, event.filingDate),
        url,
    };
};

export const companyMatchesFilters = (
    value: unknown,
    filters: Record<string, unknown>,
    filterJoin: SearchFilterJoin = 'any',
    filterOperators: Record<string, SearchFilterOperator> = {},
) => {
    const company = recordValue(value);
    const enrichment = recordValue(company.enrichment);
    const exchanges = selectedStrings(filters, 'exchanges');
    const industries = selectedStrings(filters, 'industries');
    const sectors = selectedStrings(filters, 'sectors');
    const assetClasses = selectedStrings(filters, 'assetClasses').map(
        (assetType) => LEGACY_COMPANY_ASSET_TYPE_ALIASES[assetType] ?? assetType,
    );
    const exchange = stringValue(enrichment.exchangeShortName, enrichment.exchange, company.exchange);
    const isFund = enrichment.isFund === true || enrichment.isEtf === true;
    const isIndex = Boolean(stringValue(company.symbol)?.startsWith('^'));
    const assetClass =
        company.resultType === 'nylasCompany'
            ? COMPANY_ASSET_TYPE_VALUES.private.company
            : isIndex
              ? COMPANY_ASSET_TYPE_VALUES.public.index
              : isFund
                ? COMPANY_ASSET_TYPE_VALUES.public.fund
                : COMPANY_ASSET_TYPE_VALUES.public.company;
    const applyOperator = (path: string, matchesFilter: boolean) =>
        filterOperators[path] === 'notEquals' ? !matchesFilter : matchesFilter;

    return matchesActivePredicates(
        [
            exchanges.length
                ? applyOperator('exchanges', Boolean(exchange && exchanges.includes(exchange)))
                : undefined,
            industries.length
                ? applyOperator('industries', industries.includes(String(enrichment.industry ?? '')))
                : undefined,
            sectors.length ? applyOperator('sectors', sectors.includes(String(enrichment.sector ?? ''))) : undefined,
            assetClasses.length ? applyOperator('assetClasses', assetClasses.includes(assetClass)) : undefined,
        ].filter((predicate): predicate is boolean => predicate !== undefined),
        filterJoin,
    );
};

export const documentResultMatchesFilters = (
    result: UnifiedSearchResult,
    filters: Record<string, unknown>,
    rawValue: unknown,
    filterJoin: SearchFilterJoin = 'any',
) => {
    const raw = recordValue(rawValue);
    const normalizedText = JSON.stringify(raw).toLowerCase();
    const formats = selectedStrings(filters, 'formats').map((format) => format.toLowerCase());
    const formatText = [result.title, stringValue(raw.mimeType, raw.fileType, raw.extension)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    const formatAliases: Record<string, string[]> = {
        docx: ['docx', 'msword', 'wordprocessingml'],
        epub: ['epub'],
        pdf: ['pdf'],
        sheet: ['sheet', 'spreadsheet', 'excel'],
        slide: ['slide', 'presentation', 'powerpoint'],
        text: ['text', 'plain'],
    };
    const languageCode = (language: string) => {
        const normalized = language.toLowerCase().split(/[-_]/)[0];
        return (
            (
                {
                    english: 'en',
                    spanish: 'es',
                    french: 'fr',
                    german: 'de',
                    chinese: 'zh',
                    japanese: 'ja',
                    portuguese: 'pt',
                    italian: 'it',
                    korean: 'ko',
                    arabic: 'ar',
                } as Record<string, string>
            )[normalized] ?? normalized
        );
    };
    const sources = selectedStrings(filters, 'sources').map((source) =>
        source.replace(/[^a-z0-9]/gi, '').toLowerCase(),
    );
    const resultSources = result.source.split(' / ').map((source) => source.replace(/[^a-z0-9]/gi, '').toLowerCase());
    const languages = selectedStrings(filters, 'languages').map(languageCode);
    const documentLanguages = [raw.language, raw.locale, ...arrayValue(raw.languages)].map(String).map(languageCode);
    const tags = selectedStrings(filters, 'tags').map((tag) => tag.toLowerCase());
    const documentTags = [...arrayValue(raw.tags), ...arrayValue(raw.labels)]
        .map((tag) => stringValue(recordValue(tag).name, recordValue(tag).label, tag)?.toLowerCase())
        .filter((tag): tag is string => Boolean(tag));
    const sentiments = selectedStrings(filters, 'sentiments').map((sentiment) => sentiment.toLowerCase());
    const sentiment = stringValue(raw.sentiment)?.toLowerCase();
    const pageCount = numberValue(raw.pageCount, raw.pages);
    const wordCount = numberValue(raw.wordCount, raw.words);
    const minPages = numberValue(filters.minPages);
    const maxPages = numberValue(filters.maxPages);
    const minWords = numberValue(filters.minWords);
    const maxWords = numberValue(filters.maxWords);
    const publishedAt = Date.parse(stringValue(raw.publishedAt, raw.createdTime, raw.modifiedTime) ?? '');
    const publishedFrom = Date.parse(stringValue(filters.publishedFrom) ?? '');
    const publishedTo = Date.parse(stringValue(filters.publishedTo) ?? '');
    const hasRevisions = Boolean(raw.revisionId || raw.version || arrayValue(raw.revisions).length);

    const keywords = selectedStrings(filters, 'keywords');

    return matchesActivePredicates(
        [
            formats.length
                ? formats.some((format) =>
                      (formatAliases[format] ?? [format]).some((alias) => formatText.includes(alias)),
                  )
                : undefined,
            sources.length ? sources.some((source) => resultSources.includes(source)) : undefined,
            languages.length ? languages.some((language) => documentLanguages.includes(language)) : undefined,
            tags.length ? tags.every((tag) => documentTags.includes(tag)) : undefined,
            sentiments.length ? Boolean(sentiment && sentiments.includes(sentiment)) : undefined,
            keywords.length ? keywords.every((keyword) => normalizedText.includes(keyword.toLowerCase())) : undefined,
            minPages !== undefined ? pageCount !== undefined && pageCount >= minPages : undefined,
            maxPages !== undefined ? pageCount !== undefined && pageCount <= maxPages : undefined,
            minWords !== undefined ? wordCount !== undefined && wordCount >= minWords : undefined,
            maxWords !== undefined ? wordCount !== undefined && wordCount <= maxWords : undefined,
            Number.isFinite(publishedFrom) ? Number.isFinite(publishedAt) && publishedAt >= publishedFrom : undefined,
            Number.isFinite(publishedTo)
                ? Number.isFinite(publishedAt) && publishedAt <= publishedTo + 86_399_999
                : undefined,
            filters.hasRevisions === true ? hasRevisions : undefined,
        ].filter((predicate): predicate is boolean => predicate !== undefined),
        filterJoin,
    );
};

export const workspaceResourceMatchesFilters = (
    value: unknown,
    filters: Record<string, unknown>,
    filterJoin: SearchFilterJoin = 'any',
    resourceType?: string,
    matchesSelectedTags?: boolean,
) => {
    const item = recordValue(value);
    const resourceTypes = selectedStrings(filters, 'resourceTypes');
    const selectedTags = selectedStrings(filters, 'tags').map((tag) => tag.toLowerCase());
    const itemTags = arrayValue(item.tags)
        .map((tag) => stringValue(recordValue(tag).name, recordValue(tag).title, tag)?.toLowerCase())
        .filter((tag): tag is string => Boolean(tag));
    const updatedWithinDays = { day: 1, month: 30, quarter: 90, week: 7 }[String(filters.updatedWithin)] ?? 0;
    const updatedAt = Date.parse(stringValue(item.updatedAt, item.createdAt) ?? '');

    return matchesActivePredicates(
        [
            resourceTypes.length ? Boolean(resourceType && resourceTypes.includes(resourceType)) : undefined,
            selectedTags.length
                ? (matchesSelectedTags ?? selectedTags.every((tag) => itemTags.includes(tag)))
                : undefined,
            updatedWithinDays
                ? Number.isFinite(updatedAt) && updatedAt >= Date.now() - updatedWithinDays * 86_400_000
                : undefined,
        ].filter((predicate): predicate is boolean => predicate !== undefined),
        filterJoin,
    );
};
